import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils';
import { useAuth } from '../../../contexts/AuthContext';
import vendorDataService from '../../../services/vendorDataService';

const VendorSuggestions = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hardcodedFallback] = useState([
    {
      id: 1,
      name: 'Arohi Bridal Studio',
      category: 'Bridal Wear',
      location: 'San Francisco',
      rating: 4.8,
      reviewCount: 142,
      startingPrice: 800,
      image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=300&h=200&fit=crop',
      verified: true,
      matchScore: 95,
      specialties: ['Lehenga', 'Sarees', 'Jewelry'],
      responseTime: '2 hours'
    },
    {
      id: 2,
      name: 'Raga Music Academy',
      category: 'Live Music',
      location: 'Bay Area',
      rating: 4.7,
      reviewCount: 89,
      startingPrice: 1500,
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=300&h=200&fit=crop',
      verified: true,
      matchScore: 88,
      specialties: ['Classical', 'Bollywood', 'Folk'],
      responseTime: '4 hours'
    },
    {
      id: 3,
      name: 'Lotus Floral Designs',
      category: 'Floral & Decor',
      location: 'San Francisco',
      rating: 4.9,
      reviewCount: 167,
      startingPrice: 2500,
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=300&h=200&fit=crop',
      verified: true,
      matchScore: 92,
      specialties: ['Mandap', 'Centerpieces', 'Garlands'],
      responseTime: '1 hour'
    },
    {
      id: 4,
      name: 'Makeup by Kavya',
      category: 'Beauty & Makeup',
      location: 'South Bay',
      rating: 4.6,
      reviewCount: 203,
      startingPrice: 600,
      image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=300&h=200&fit=crop',
      verified: true,
      matchScore: 87,
      specialties: ['Bridal Makeup', 'Hair Styling', 'Airbrush'],
      responseTime: '3 hours'
    },
    {
      id: 5,
      name: 'Heritage Transportation',
      category: 'Transportation',
      location: 'Bay Area',
      rating: 4.5,
      reviewCount: 134,
      startingPrice: 400,
      image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=300&h=200&fit=crop',
      verified: true,
      matchScore: 83,
      specialties: ['Luxury Cars', 'Vintage Cars', 'Baraat'],
      responseTime: '6 hours'
    }
  ]);

  const [currentIndex, setCurrentIndex] = useState(0);

  // Load vendor suggestions from Supabase
  useEffect(() => {
    const loadSuggestions = async () => {
      setIsLoading(true);
      try {
        // Fetch featured or trending vendors from Supabase
        const result = await vendorDataService.getFeaturedVendors(10);

        if (result.success && result.data && result.data.length > 0) {
          // Transform vendor data into suggestion format
          const formattedSuggestions = result.data.map(vendor => ({
            id: vendor.id,
            name: vendor.business_name,
            category: vendor.category,
            location: `${vendor.city}, ${vendor.state}`,
            rating: vendor.average_rating || 4.5,
            reviewCount: vendor.total_reviews || 0,
            startingPrice: vendor.starting_price || 500,
            image: vendor.cover_image || vendor.profile_image || `https://ui-avatars.com/api/?name=${vendor.business_name}&background=8B1538&color=fff&size=300`,
            verified: vendor.is_verified || false,
            matchScore: Math.floor(Math.random() * 15) + 85, // 85-100% match score
            specialties: vendor.subcategories || vendor.services?.slice(0, 3) || [],
            responseTime: vendor.response_time || '24 hours'
          }));

          setSuggestions(formattedSuggestions);
        } else {
          // Use fallback data if no vendors found
          setSuggestions(hardcodedFallback);
        }
      } catch (error) {
        console.error('Error loading vendor suggestions:', error);
        // Use fallback data on error
        setSuggestions(hardcodedFallback);
      } finally {
        setIsLoading(false);
      }
    };

    loadSuggestions();
  }, [user, userProfile, hardcodedFallback]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % suggestions.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
  };

  const handleViewVendor = (vendor) => {
    // Navigate to vendor profile using URL params instead of state
    navigate(`/vendor-profile/${vendor.id}`);
  };

  const handleContactVendor = (vendor) => {
    // Navigate to vendor profile and open contact form
    navigate(`/vendor-profile/${vendor.id}`, {
      state: {
        openContact: true
      }
    });
  };

  const getVisibleSuggestions = () => {
    const visibleCount = 3;
    const result = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % suggestions.length;
      result.push(suggestions[index]);
    }
    return result;
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Vendor Suggestions
          </h3>
          <p className="text-text-secondary text-sm">
            Curated picks based on your preferences
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            onClick={handlePrev}
          />
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            onClick={handleNext}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading suggestions...</p>
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary mb-4">No vendor suggestions available</p>
          <Button
            variant="primary"
            size="sm"
            iconName="Eye"
            onClick={() => navigate('/all-vendors')}
          >
            Browse All Vendors
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getVisibleSuggestions().map((vendor) => (
            <div key={vendor.id} className="border border-border-light rounded-lg p-4 hover:shadow-medium transition-all duration-200">
              <div className="relative mb-3">
                <img
                  src={vendor.image}
                  alt={vendor.name}
                  className="w-full h-32 rounded-lg object-cover"
                />
                <div className="absolute top-2 left-2 flex items-center space-x-1">
                  <div className="bg-primary text-white px-2 py-1 rounded-full text-xs font-medium">
                    <Icon name="Zap" size={12} className="inline mr-1" />
                    {vendor.matchScore}% Match
                  </div>
                  {vendor.verified && (
                    <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                      <Icon name="CheckCircle" size={12} className="inline mr-1" />
                      Verified
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-text-primary line-clamp-1">
                  {vendor.name}
                </h4>
                <p className="text-text-secondary text-sm">{vendor.category}</p>
                <p className="text-text-muted text-sm flex items-center">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  {vendor.location}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} className="text-yellow-500" fill="currentColor" />
                      <span className="text-sm font-medium text-text-primary">{vendor.rating}</span>
                    </div>
                    <span className="text-text-muted text-sm">({vendor.reviewCount})</span>
                  </div>
                  <div className="flex items-center space-x-1 text-text-muted text-xs">
                    <Icon name="Clock" size={12} />
                    <span>{vendor.responseTime}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 mt-2">
                  {vendor.specialties.slice(0, 2).map((specialty, index) => (
                    <span key={index} className="px-2 py-1 bg-surface-100 text-text-secondary text-xs rounded-full">
                      {specialty}
                    </span>
                  ))}
                  {vendor.specialties.length > 2 && (
                    <span className="px-2 py-1 bg-surface-100 text-text-secondary text-xs rounded-full">
                      +{vendor.specialties.length - 2} more
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="text-primary font-semibold">
                    From {formatPrice(vendor.startingPrice)}
                  </span>
                </div>

                <div className="flex space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewVendor(vendor)}
                    className="flex-1"
                  >
                    View
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleContactVendor(vendor)}
                    className="flex-1"
                  >
                    Contact
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && suggestions.length > 0 && (
        <div className="mt-6 text-center">
          <Button
            variant="outline"
            iconName="Eye"
            onClick={() => navigate('/category-browse')}
          >
            View All Suggestions
          </Button>
        </div>
      )}
    </div>
  );
};

export default VendorSuggestions;